// utils/generateTagsAndCategories.js

/**
 * Automatically generates tags and categories based on POI data
 * @param {Array} pois - Array of POI objects
 * @returns {Object} Object containing tags and categories arrays
 */
const generateTagsAndCategories = (pois) => {
    if (!pois || pois.length === 0) {
      return {
        tags: [],
        categories: []
      };
    }
  
    // Extract all categories from POIs (remove duplicates)
    const categories = [...new Set(
      pois
        .map(poi => poi.category)
        .filter(category => category) // Remove empty categories
    )];
  
    // Generate tags based on POI names, descriptions, and categories
    const tagSources = pois.flatMap(poi => {
      const sources = [];
      
      // Add name keywords as potential tags
      if (poi.name) {
        const nameWords = poi.name.split(/\s+/).filter(word => word.length > 3);
        sources.push(...nameWords);
      }
      
      // Add category as a tag source
      if (poi.category) sources.push(poi.category);
      
      // Extract keywords from description
      if (poi.description) {
        // Split description into words and filter for potential keywords
        const words = poi.description.split(/\s+/);
        const potentialKeywords = words.filter(word => 
          // Select words that might be good tags (longer words, proper nouns, etc.)
          word.length > 5 && 
          !commonWords.includes(word.toLowerCase()) &&
          word.match(/^[A-Z]/) // Starts with capital letter (proper nouns)
        );
        sources.push(...potentialKeywords);
      }
      
      // Add historical/cultural elements if available
      if (poi.historicalContext) {
        // Extract period references (century, era, etc.)
        const periodMatches = poi.historicalContext.match(/\d+(st|nd|rd|th)\s+century|ancient|medieval|renaissance|baroque|modern|gothic/gi);
        if (periodMatches) sources.push(...periodMatches);
      }
      
      // Extract themes from cultural significance
      if (poi.culturalSignificance) {
        const culturalKeywords = extractKeywords(poi.culturalSignificance);
        sources.push(...culturalKeywords);
      }
      
      return sources;
    });
  
    // Get unique tags, clean them, and limit to a reasonable number
    const rawTags = [...new Set(tagSources)];
    
    // Clean tags (remove punctuation, normalize spacing)
    const cleanedTags = rawTags
      .map(tag => tag.replace(/[.,;:()"']/g, '').trim())
      .filter(tag => tag.length > 2) // Remove very short tags
      .filter(tag => !categories.includes(tag)); // Don't duplicate categories as tags
      
    // Select the most relevant tags (limit to 15)
    const tags = [...new Set(cleanedTags)].slice(0, 15);
  
    return {
      tags,
      categories
    };
  };
  
  // Helper function to extract meaningful keywords from text
  const extractKeywords = (text) => {
    if (!text) return [];
    
    // Split text into sentences
    const sentences = text.split(/[.!?]+/);
    
    // Look for patterns that often contain meaningful keywords
    const keywords = [];
    
    sentences.forEach(sentence => {
      // Look for phrases like "known for X", "famous for X"
      const knownForMatch = sentence.match(/known for (.+?)(?:,|and|$)/i);
      if (knownForMatch && knownForMatch[1]) {
        keywords.push(knownForMatch[1].trim());
      }
      
      // Look for phrases like "represents X", "symbolizes X"
      const representsMatch = sentence.match(/represents|symbolizes|exemplifies (.+?)(?:,|and|$)/i);
      if (representsMatch && representsMatch[1]) {
        keywords.push(representsMatch[1].trim());
      }
    });
    
    return keywords;
  };
  
  // Common words to exclude from tags
  const commonWords = [
    'the', 'and', 'that', 'have', 'for', 'not', 'with', 'you',
    'this', 'but', 'his', 'from', 'they', 'say', 'her', 'she',
    'will', 'one', 'all', 'would', 'there', 'their', 'what',
    'about', 'which', 'when', 'make', 'like', 'time', 'just',
    'know', 'people', 'year', 'take', 'them', 'some', 'into',
    'your', 'good', 'more', 'place', 'visit', 'located', 'famous',
    'important', 'interesting', 'beautiful', 'popular', 'traditional',
    'historical', 'cultural'
  ];
  
  export default generateTagsAndCategories;